import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity, { Address, BusinessCategory, BusinessType, ProviderID } from '../../../core/models/business.model';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import FarmSiteModel, { IAddress, ISoilType, IWaterType } from '../../../core/models/farm-site.model';
import FeedLogModel from '../../../core/models/feedlog.model';
import PondCultureModel from '../../../core/models/pond-culture.model';
import PersonsModel from '../../../core/models/persons.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BusinessTypeService } from '../../../core/services/business-type.service';
import { BusinessCategoryService } from '../../../core/services/business-category.service';
import BusinessCategoryModel from '../../../core/models/business-category.model';
import BusinessTypeModel from '../../../core/models/business-type.model';
import WaterTypeModel from '../../../core/models/water-type.model';
import SoilTypeModel from '../../../core/models/soil-typeModel';
import { WaterTypeService } from '../../../core/services/water-type.service';
import { SoilTypeService } from '../../../core/services/soil-type.service';
import CustomerLeadModel from '../../../core/models/customer-lead.model';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { CountriesAPIService } from '../../../core/services/countries.service';
import CountriesModel from '../../../core/models/countries.model';
import { USER_SESSION_SERVICE } from 'src/app/core/rbacl/user-session.service';

declare var $: any;

/**
 * @component BusinessEntityFarmProviderComponent
 * @selector app-business-entity-farm-provider
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BusinessEntityFarmProviderComponent` manages the lifecycle and relationships of Farm Provider business entities.
 * It serves as a central hub for creating, updating, and viewing business profiles for providers and their associated farms.
 *
 * ## Key Responsibilities:
 * 1. **Business Entity Management**: Handles CRUD operations for business entities (Farms, Providers).
 * 2. **Farm Site Association**: Manages the creation and linking of farm sites to business entities.
 * 3. **Role-Based Access**: Adapts functionality and data visibility based on user roles (Business Admin, Provider Manager, Employee).
 * 4. **Performance Metrics**: Visualizes farm performance data including Feed vs DOC and ABW vs DOC charts.
 * 5. **Location Management**: Provides hierarchical location selection (Country -> State -> Village) with infinite scroll and geocoding.
 * 6. **Lead Generation**: Converts business registrations into customer leads.
 *
 * ## User Roles & Permissions:
 * - **Business Admin (Provider)**: Can manage their provider business and view associated farmers.
 * - **Provider Manager**: Can view farms assigned to their technicians.
 * - **Employee**: System-wide access to manage all business entities.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: Logged-in business ID.
 * - `personID`: Logged-in user ID.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` loads user context and fetches initial business lists based on role.
 * 2. **Business Selection**: `getBusinessProfileById` fetches detailed profile, farm sites, and metrics for a selected business.
 * 3. **Form Handling**: `addBusinessDetailsSubmit` and `farmProviderUpdate` handle form submissions for creating/updating businesses.
 * 4. **Farm Site Creation**: `addFarmSite` handles the creation of new farm sites linked to the business.
 *
 * @service BusinessEntityService - For business entity CRUD.
 * @service FarmSiteService - For farm site management.
 * @service FeedLogService - For fetching performance metrics.
 * @service PondCultureService - For culture data and charts.
 * @service CountriesAPIService - For location data.
 * @service CommonMethodsService - For error handling.
 */
@Component({
  selector: 'app-business-profile',
  templateUrl: './business-profile.component.html',
  styleUrls: ['./business-profile.component.scss']
})
export class BusinessProfileComponent implements OnInit, OnDestroy {
  /** @type {BusinessEntity} The main business entity model being viewed or edited. */
  public businessEntity: BusinessEntity;

  /** @type {BusinessEntity} Model for registering a new business. */
  public registerBusiness: BusinessEntity;

  /** @type {Array<BusinessEntity>} List of available business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<CustomerLeadModel>} List of customer leads. */
  public customerLeads: Array<CustomerLeadModel>;

  /** @type {CustomerLeadModel} Current customer lead model. */
  public customerLead: CustomerLeadModel;

  /** @type {Address} Address model for the business. */
  public address: Address;

  /** @type {Array<CustomerPondModel>} List of customer ponds. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {CustomerPondModel} Single customer pond model. */
  public customerPond: CustomerPondModel;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Single farm site model. */
  public farmSite: FarmSiteModel;

  /** @type {Array<FeedLogModel>} List of feed logs. */
  public feedLogs: Array<FeedLogModel>;

  /** @type {FeedLogModel} Single feed log model for metrics. */
  public feedLog: FeedLogModel;

  /** @type {Array<PersonsModel>} List of persons. */
  public persons: Array<PersonsModel>;

  /** @type {PersonsModel} Single person model. */
  public person: PersonsModel;

  /** @type {PondCultureModel} Pond culture model for charts. */
  public pondCulture: PondCultureModel;

  /** @type {Array<BusinessCategoryModel>} List of business categories. */
  public businessCategories: Array<BusinessCategoryModel>;

  /** @type {Array<BusinessTypeModel>} List of business types. */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {BusinessType} Selected business type. */
  public businessType: BusinessType;

  /** @type {BusinessCategory} Selected business category. */
  public businessCategory: BusinessCategory;

  /** @type {ProviderID} Provider ID model. */
  public providerId: ProviderID;

  /** @type {IAddress} Address model for farm site. */
  public farmSiteAddress: IAddress;

  /** @type {Array<WaterTypeModel>} List of water types. */
  public waterTypes: Array<WaterTypeModel>;

  /** @type {Array<SoilTypeModel>} List of soil types. */
  public soilTypes: Array<SoilTypeModel>;

  /** @type {IWaterType} Selected water type. */
  public waterType: IWaterType;

  /** @type {ISoilType} Selected soil type. */
  public soilType: ISoilType;

  /** @type {FeedLogModel} Feed log model for pond level data. */
  public pondLevelFeedLog: FeedLogModel;

  /** @type {FormGroup} Reactive form for business registration. */
  public registerForm: FormGroup;

  /** @type {BusinessEntity} Pincode validation result. */
  public pinCodes: BusinessEntity;

  /** @type {FormGroup} Reactive form for adding farm sites. */
  public addFarmsiteForm: FormGroup;

  /** @type {string} Logged-in user ID. */
  public loginPersonID: string;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {boolean} Response loading state flag. */
  public responseLoader = false;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {Array<any>} List of farm site IDs. */
  public farmsiteIds = new Array();

  /** @type {string} Selected farm site ID. */
  public farm_site_id: string;

  /** @type {any} Selected pond ID. */
  public pond_id: any;

  /** @type {any[]} Dropdown list for active ponds. */
  public activepondsDetails_dropdown: any[];

  /** @type {any} Chart canvas element. */
  public canvas: any;

  /** @type {any} Chart context. */
  public ctx: any;

  /** @type {any} Document object. */
  public doc: any;

  /** @type {boolean} Flag for chart data availability. */
  public myChartNodata: boolean;

  /** @type {boolean} Flag for sampling chart data availability. */
  public mySamplingvsDocData: boolean;

  /** @type {number} Total area count. */
  public areaCount: number;

  /** @type {string} Logged-in user business name. */
  public loginUserBusinessName: string;

  /** @type {boolean} Flag indicating form submission. */
  public submitted: boolean;

  /** @type {Array<any>} List of countries. */
  public countries = new Array();

  /** @type {Object} Generic item object. */
  public item: Object;

  /** @type {string} Search keyword. */
  public keyword: string;

  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;

  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;

  /** @type {boolean} Flag to show farm site form. */
  public showsiteform: boolean;

  // Validation form declaration
  /** @type {boolean} Flag indicating farm site form submission. */
  public farmSitesubmitted = false;

  /** @type {Date} Start date for contracts. */
  public startDate: Date;

  /** @type {Date} End date for contracts. */
  public endDate: Date;

  /** @type {string} Country code. */
  public countryCode: string;

  /** @type {Array<any>} Array of years for dropdown. */
  public yearStartedArr = new Array();

  /** @type {Array<any>} List of villages. */
  public villageList = new Array();

  /** @type {BusinessEntity} Logged-in business entity. */
  public loginBusinessEntity: BusinessEntity;

  /** @type {BusinessCategoryModel[]} Duplicate list of business categories. */
  public dupbusinessCategories: BusinessCategoryModel[];

  /** @type {BusinessTypeModel[]} Duplicate list of business types. */
  public dupbusinessTypes: BusinessTypeModel[];

  /** @type {BusinessEntity} Selected business entity for viewing. */
  public selectedBusinessEntity: BusinessEntity;

  /** @type {Array<PersonsModel>} List of business admin persons. */
  public businessAdminPersons: Array<PersonsModel>;

  /** @type {any} Application permissions. */
  public appPermission: any;

  /** @type {number} Count of farms for business admin. */
  public FarmsForBusinessAdmin: number;

  /** @type {FarmSiteModel} Lat/Long data. */
  public latLongs: FarmSiteModel;

  /** @type {Array<any>} List of states. */
  public states: Array<any>;

  /** @type {Array<any>} List of villages. */
  public villages: Array<any>;

  /** @type {Array<any>} Filtered states list. */
  public filteredStates = new Array();

  /** @type {Array<any>} Filtered villages list. */
  public filteredVillages = new Array();

  /** @type {any[]} Limited villages list for pagination. */
  public limitedVillagesList: any[] = [];

  /** @type {Object} Selected state item. */
  public stateitem: Object;

  /** @type {Object} Selected village item. */
  public villageItem: Object;

  /** @type {string} State search keyword. */
  public statekeyword: string;

  /** @type {string} Village search keyword. */
  public villagekeyword: string;

  /** @type {string} Selected country name. */
  public country: string;

  /** @type {boolean} Flag indicating village change. */
  public villageChanged: boolean = false;

  /** @type {boolean} Flag to disable state/village fields. */
  public stateVillageDisabled: boolean = false;

  /** @type {boolean} Flag to prevent double clicks on provider business. */
  public providerBusinessDoubleClick: boolean;

  /** @type {boolean} Flag to prevent double clicks on location. */
  public locationDoubleClick: boolean;

  /** @type {string} Logged-in business category. */
  public loginBusinessCategory: string;

  /** @type {boolean} Flag for dropdown state. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for dropdowns. */
  public filteredOptions: any[] = [];

  /** @type {string} Country search text. */
  public countrySearch: string = 'Enter country name';

  /** @type {string} Logged-in business ID. */
  public loginBusinessID: string;

  /** @type {string} Feed provider person ID. */
  public feedProviderPersonID: string;

  /** @type {string} Processed farm name. */
  public finalFarmName: string;

  /** @type {number} Count of form fields. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show/hide form. */
  public showForm: boolean = false;

  /**
   * Constructor for BusinessEntityFarmProviderComponent.
   * Initializes services and reactive forms.
   *
   * @param {StatesAPIService} statesAPIService - Service for state data.
   * @param {CountriesAPIService} countriesAPIService - Service for country data.
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {CustomerLeadService} customerLeadService - Service for customer leads.
   * @param {WaterTypeService} waterTypeService - Service for water types.
   * @param {SoilTypeService} soilTypeService - Service for soil types.
   * @param {FarmSiteService} farmSiteService - Service for farm sites.
   * @param {BusinessTypeService} businessTypeService - Service for business types.
   * @param {BusinessCategoryService} businessCategoryService - Service for business categories.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {PersonsService} personService - Service for persons.
   * @param {PondCultureService} pondCultureService - Service for pond culture.
   * @param {FeedLogService} feedlogService - Service for feed logs.
   * @param {FarmSiteService} farmsiteService - Service for farm sites (duplicate injection).
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {ActivatedRoute} route - Active route service.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {Router} router - Angular Router.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {NgZone} ngZone - Angular Zone service.
   * @param {ChangeDetectorRef} cdr - Change detector reference.
   */
  constructor(public countriesAPIService: CountriesAPIService, private toasterService: ToastrService, public waterTypeService: WaterTypeService, public soilTypeService: SoilTypeService, public businessTypeService: BusinessTypeService, public businessCategoryService: BusinessCategoryService, public formBuilder: FormBuilder, public businessEntityService: BusinessEntityService, public route: ActivatedRoute, public commonMethodsService: CommonMethodsService,
    public router: Router, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.registerBusiness = new BusinessEntity();
    this.address = new Address();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.waterType = new IWaterType();
    this.soilType = new ISoilType();
    this.villageList = new Array();
    this.showsiteform = false;
    // Business Details registraion validation
    this.registerForm = this.formBuilder.group({
      businessName: ['', [Validators.required,
      Validators.minLength(5),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z ]*$')
      ]],
      businessCode: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(5),
        Validators.pattern('^[a-zA-Z0-9-]+$')
      ]],
      businessType: ['', Validators.required],
      email: ['', [Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,6}$')
      ]],
      mobilenumber: ['', Validators.required],
      countryName: ['', [Validators.required]],
      category: ['', [Validators.required]],
      dialCode: ['', [Validators.required]]
    });

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes form state and flags.
   * - Fetches reference data (Countries, Business Types, etc.).
   * - Loads business entities based on user role.
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.pgLoad = false;
    this.providerBusinessDoubleClick = false;
    this.closeForm();
    this.submitButton = false;
    this.updateButton = false;
    this.countries = await this.countriesAPIService.getAllCountriesList();
    this.countrySearch = 'India';
    this.address.country = 'India';
    if (this.countries.length > 0) {
      this.filteredOptions = this.countries;
      this.getDialCode();
    }
    // loginperson type details
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.loginPersonID = localStorage.getItem('personID');
    this.loginBusinessID = localStorage.getItem('businessID');
    this.responseLoader = true;
    this.areaCount = 0;
    this.yearStartedArr = new Array();
    this.businessType.businessTypeID = '';
    this.businessCategory.businessCategoryID = '';
    this.businessCategory.name = '';
    this.businessType.name = '';
    let curryear = new Date().getFullYear();
    for (let i = 1947; i < curryear; i++) {
      this.yearStartedArr.push(i + 1);
    }
    // end get year started
    try {
      this.businessEntity = await this.businessEntityService.getOneByID(USER_SESSION_SERVICE.currentBusinessId);
      this.businessTypes = [];
      this.businessCategories = [];
      this.dupbusinessTypes = [];
      this.dupbusinessCategories = [];
      this.businessCategories = (await this.businessCategoryService.getAllBusinessCategories()).filter(obj => ['61890be727c16c001eb0d9a6', '61890d9027c16c001eb0d9a7'].includes(obj.id)); // Only farm and provider categories are being filtered
      this.businessTypes = await this.businessTypeService.getAllBusinessTypes();
      this.dupbusinessCategories = this.businessCategories;
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getBusinessTypesByCategory
   * @description Filters business types based on the selected business category.
   * Auto-selects if only one type is available.
   */
  public getBusinessTypesByCategory() {
    this.dupbusinessTypes = this.businessTypes.filter((obj) => obj.businessCategoryID === this.businessCategory.businessCategoryID);
    if (this.dupbusinessTypes.length == 1) {
      this.businessType.businessTypeID = this.dupbusinessTypes[0].id;
    } else {
      this.businessType.businessTypeID = '';
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for BusinessEntityService, BusinessCategoryService,
   * BusinessTypeService, WaterTypeService, SoilTypeService, and CustomerLeadService.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.soilTypeService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getDialCode
   * @description Retrieves and sets the dialing code for the selected country.
   * @side-effect Updates `registerBusiness.dialingCode`.
   */
  public getDialCode() {
    let item = this.countries.find(code => code.name == this.address.country);
    this.registerBusiness.dialingCode = item.dialingCode[0];
  }

  /**
   * @method f
   * @description Getter for easy access to `registerForm` controls.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method getBusinessCode
   * @async
   * @description Fetches business code suggestions based on the business name.
   */
  public async getBusinessCode() {
    if (this.registerBusiness.businessName != '' && this.registerBusiness.businessName != undefined) {
      try {
        const businessCodes = await this.businessEntityService.getBusinessCodeSuggestions(this.processFarmName(this.registerBusiness.businessName), 'noCode');
        if (businessCodes && businessCodes['suggestions'].length > 0) {
          this.registerBusiness.businessCode = businessCodes['suggestions'][0];
        }
      } catch (error) {
      }
    }
  }

  /**
   * @method onBusinessChange
   * @description Clears business code if business name is empty.
   */
  public onBusinessChange() {
    if (this.registerBusiness.businessName == '' || this.registerBusiness.businessName == undefined) {
      this.registerBusiness.businessCode = '';
    }
  }

  /**
   * @method processFarmName
   * @description Formats the farm name to CamelCase for code generation.
   * @param {string} farmName - The raw farm name.
   */
  public processFarmName(farmName: string) {
    let trimmedFarmName = farmName.trim();
    if (trimmedFarmName.includes(' ')) {
      this.finalFarmName = trimmedFarmName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      return this.finalFarmName;
    } else {
      return trimmedFarmName;
    }
  }

  /**
   * @method openForm
   * @description Opens the form for creating a new business entity.
   * Sets default values (e.g., Country = India).
   */
  public openForm() {
    this.submitButton = true;
    this.updateButton = false;
    this.showsiteform = false;
    this.showForm = true;
    const countryObj = this.countries?.find(country => country.name == 'India');
    this.countrySelected(countryObj);
  }

  /**
   * @method getBusinessEntityById
   * @async
   * @description Fetches business entity details by ID for editing.
   * @param {any} id - The ID of the business entity.
   */
  public async getBusinessEntityById(id) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.providerBusinessDoubleClick = false;
    this.registerBusiness = await this.businessEntityService.getOneByID(id);
    if (this.registerBusiness.address.length > 0) {
      this.address.country = this.registerBusiness.address[0].country;
      this.countrySearch = this.registerBusiness.address[0].country;
      this.registerForm.get('countryName')!.setValue(this.countrySearch);
      this.getDialCode();
    }
    this.businessCategories = this.dupbusinessCategories;
    if (this.registerBusiness.businessCategory.length > 0) {
      this.businessCategory.businessCategoryID = this.registerBusiness.businessCategory[0].businessCategoryID;
      this.getBusinessTypesByCategory();
    }
    if (this.registerBusiness.businessType.length > 0) {
      this.businessType.businessTypeID = this.registerBusiness.businessType[0].businessTypeID;
    }
    if (this.registerBusiness.address.length == 0) {
      this.registerBusiness.address.push({
        houseNo: '',
        street: '',
        landMark: '',
        state: '',
        district: '',
        city: '',
        pincode: '',
        countryCode: '',
        country: this.address.country
      });
    }
    // return this.registerBusiness;
  }

  /**
   * @method farmProviderUpdate
   * @async
   * @description Updates an existing business entity.
   */
  public async farmProviderUpdate() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.providerBusinessDoubleClick = true;
    const type = this.businessTypes.find(btype => btype.id == this.businessType.businessTypeID);
    const category = this.businessCategories.find(bCategory => bCategory.id == this.businessCategory.businessCategoryID);
    console.log(650, type, this.businessEntity.businessType, this.businessTypes),
      this.registerBusiness.businessType = [
        {
          businessTypeID: type.id,
          name: type.name
        }
      ];
    this.registerBusiness.businessCategory = [
      {
        name: category.name,
        businessCategoryID: category.id
      }
    ];
    this.countrySearch = this.registerBusiness.address[0].country;
    const item = this.countries.find(countryName => countryName.name == this.countrySearch);
    this.registerBusiness.address[0].country = item.name;
    this.registerBusiness.address[0].countryCode = item.countryCode;
    try {
      this.submitted = false;
      const updateRegisterBusiness = Object.assign({}, this.registerBusiness);
      delete updateRegisterBusiness.id;
      this.registerBusiness = await this.businessEntityService.updateOneByID(this.registerBusiness.id, updateRegisterBusiness);
      this.toasterService.success('Business entity updated successfully.', '', {
        timeOut: 2000
      });
      this.showForm = false;
      this.updateButton = false;
      this.registerBusiness = new BusinessEntity();
      this.businessType.businessTypeID = '';
      this.businessCategory.businessCategoryID = '';
      this.businessCategory.name = '';
      this.businessType.name = '';
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.providerBusinessDoubleClick = false;
    }

  }

  /**
   * @method closeForm
   * @description Resets the form and component state to default.
   */
  public closeForm() {
    this.registerForm.reset();
    this.showForm = false;
    this.registerBusiness = new BusinessEntity();
    this.address = new Address();
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.villageChanged = false;
    this.stateVillageDisabled = false;
    this.countrySearch = 'India';
    this.providerBusinessDoubleClick = false;
    this.businessType.businessTypeID = '';
    this.businessCategory.businessCategoryID = '';
    this.businessCategory.name = '';
    this.businessType.name = '';
  }

  public filterCountries(): void {
    const value = (this.registerForm.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredOptions = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method countrySelected
   * @description Handles country selection. Updates form and fetches states.
   * @param {any} country - The selected country object.
   */
  public countrySelected(country: any): void {
    this.registerForm.get('countryName')!.setValue(country.name);
    this.isDropdownOpen = false;
    this.countrySearch = country.name;
    this.address.country = country.name;
    this.businessEntity.address[0].country = country.name;
    this.registerBusiness.dialingCode = country.dialCode;
    this.registerForm.get('mobilenumber').clearValidators();
    switch (country.name) {
      case 'Spain':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Vietnam':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Nigeria':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Australia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
      default:
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        break;
    }
    this.getDialCode();
  }

  /**
   * @method navigateToAccountClosurePopupOpenScreen
   * @description Navigates to the account closure screen.
   */
  public navigateToAccountClosurePopupOpenScreen() {
    this.router.navigate(['/home/account-closure']);
  }
}
